const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const getSafeString = (value, fallback = '') => {
  if (typeof value !== 'string') return fallback;
  const clean = value.trim();
  return clean || fallback;
};

export const getSafeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const getSafeArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

export const normalizeCampaignName = (name) => {
  const raw = getSafeString(name, 'Yeni Kampanya');
  const lower = raw.toLocaleLowerCase('tr-TR');
  if (lower.includes('anneler')) return 'Anneler Günü Kampanyası';
  if (lower.includes('sevgililer')) return 'Sevgililer Günü Kampanyası';
  if (lower.includes('bayram')) return 'Bayram Kampanyası';
  return raw;
};

export const calculateCampaignHealth = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (!safeRows.length) return 65;
  const done = safeRows.filter((r) => getSafeString(r?.durum) === 'Tamamlandı').length;
  const completionRate = done / safeRows.length;
  return Math.max(30, Math.min(100, Math.round(40 + completionRate * 60)));
};

export const detectCampaignType = (campaignName) => {
  const lower = normalizeCampaignName(campaignName).toLocaleLowerCase('tr-TR');
  if (lower.includes('anneler')) return 'mothers-day';
  if (lower.includes('sevgililer')) return 'valentine';
  if (lower.includes('bayram')) return 'holiday';
  return 'generic';
};

const getSecondSundayOfMay = (year) => {
  const d = new Date(year, 4, 1);
  while (d.getDay() !== 0) d.setDate(d.getDate() + 1);
  d.setDate(d.getDate() + 7);
  return d;
};

const toTRDate = (date) => date.toLocaleDateString('tr-TR');

export const generateCampaignDateRange = (campaignName, currentDate = new Date()) => {
  const type = detectCampaignType(campaignName);
  const year = currentDate.getFullYear();

  if (type === 'mothers-day') {
    const anchor = getSecondSundayOfMay(year);
    const start = new Date(anchor);
    start.setDate(anchor.getDate() - 7);
    return { start, end: anchor, reason: 'Anneler Gunu icin 7 gun once baslatildi' };
  }

  if (type === 'valentine') {
    return {
      start: new Date(year, 1, 7),
      end: new Date(year, 1, 14),
      reason: 'Sevgililer Gunu icin 7-14 Subat araligi onerildi'
    };
  }

  if (type === 'holiday') {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    end.setDate(currentDate.getDate() + 6);
    return { start, end, reason: 'Bayram oncesi hazirlik haftasi onerildi' };
  }

  const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 5);
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), 11);
  return { start, end, reason: 'Genel kampanya haftasi onerildi' };
};

export const generateDailyCampaignPlan = (campaignName, dateRange) => {
  const safeName = normalizeCampaignName(campaignName);
  const goal = getSafeString(dateRange?.reason, 'Kampanya ürünlerini görünür kılmak ve günlük satış disiplinini artırmak');
  const goalLower = goal.toLocaleLowerCase('tr-TR');
  const isGift = goalLower.includes('hediye') || goalLower.includes('anneler') || goalLower.includes('sevgili');
  const isSales = goalLower.includes('satış') || goalLower.includes('ciro') || goalLower.includes('hedef');
  const isStock = goalLower.includes('stok') || goalLower.includes('ürün') || goalLower.includes('urun');
  const start = dateRange?.start instanceof Date ? dateRange.start : new Date();
  const end = dateRange?.end instanceof Date ? dateRange.end : new Date(start);
  const plan = [];
  const stages = ['Hazirlik', 'Vitrin', 'Sosyal', 'Satis Push', 'Optimizasyon', 'Takip', 'Kapanis'];
  const taskSets = {
    magaza: [
      isGift ? 'Hediye odaklı ürünleri giriş ve vitrine çıkar' : 'Kampanya ürünlerini vitrine çıkar',
      isStock ? 'Stoklu ürünleri görünür alana taşı' : 'Etiket ve fiyat kartlarını kontrol et',
      'Banko teklif kartlarını hazırla',
      'Vitrin eksiklerini güncelle'
    ],
    personel: [
      `Ekibe hedefi aktar: ${goal}`,
      isGift ? 'Müşteriye hediye önerisiyle yaklaş' : 'Müşteri karşılama cümlesini paylaş',
      isSales ? 'Günlük satış hedefini personele bildir' : 'Ürün faydasını kısa anlat',
      'Gün sonu geri bildirim al'
    ],
    internet: [
      isSales ? 'Webde dönüşüm odaklı kampanya bloğu aç' : 'Ana sayfa bannerını kontrol et',
      'Kampanya ürünlerini webde öne çıkar',
      'Ürün sayfalarını ve açıklamaları kontrol et',
      'Kampanya bloğunu güncelle'
    ],
    instagram: [
      isGift ? 'Hediye önerili story serisi paylaş' : 'Story duyurusu paylaş',
      'Reels paylaşımı hazırla',
      'DM yönlendirme mesajını kontrol et',
      'Kampanya ürününü storyde öne çıkar'
    ]
  };
  let cursor = new Date(start);
  let idx = 0;

  while (cursor <= end) {
    const stage = stages[idx % stages.length];
    const magaza = taskSets.magaza[idx % taskSets.magaza.length];
    const personel = taskSets.personel[idx % taskSets.personel.length];
    const internet = taskSets.internet[idx % taskSets.internet.length];
    const instagram = taskSets.instagram[idx % taskSets.instagram.length];
    plan.push({
      date: toTRDate(cursor),
      campaign: `${safeName} - ${stage}`,
      detail: [
        `Günün amacı: ${goal}. ${stage} aşamasında müşterinin kampanyayı kolay anlaması ve ürünleri hızlı görmesi hedeflenir.`,
        `Mağaza/Vitrin yapılacakları: ${magaza}. Ürünler müşterinin ilk göreceği alanda temiz, düzenli ve etiketleri okunur şekilde hazırlanmalı.`,
        `Personel görevi: ${personel}. Personel müşteriye kısa, nazik ve fayda odaklı anlatım yapmalı.`,
        `Müşteriye söylenecek cümle: Bugün kampanyalı ürünlerimizi ve size uygun avantajlı seçenekleri göstermek isteriz.`,
        `Yönetici notu: ${internet}; ${instagram}. Dijital görünürlük ve mağaza içi uygulama aynı mesajla ilerlemeli.`,
        'Gün sonu kontrolü: Tamamlanan görevler, eksikler, müşteri tepkisi ve ihtiyaç duyulan ürün/paket malzemesi gün sonunda yöneticiye aktarılmalı.'
      ].join('\n'),
      magaza,
      personel,
      internet,
      instagram,
      durum: 'Yapılacak'
    });
    cursor = new Date(cursor.getTime() + MS_PER_DAY);
    idx += 1;
  }
  return plan;
};

export const generateSmartActions = ({ campaignName, rows, storePerformance, salesData }) => {
  const safeStore = storePerformance && typeof storePerformance === 'object' ? storePerformance : {};
  const safeSales = salesData && typeof salesData === 'object' ? salesData : {};
  const actions = [];

  const salesScore = getSafeNumber(safeStore.salesScore, 0);
  const instagramScore = getSafeNumber(safeStore.instagramScore, 0);
  const displayScore = getSafeNumber(safeStore.displayScore, 0);
  const staffScore = getSafeNumber(safeStore.staffScore, 0);
  const lastCampaignResult = getSafeString(safeStore.lastCampaignResult, 'unknown');
  const trend = getSafeString(safeSales.trend, 'stable');
  const totalSales = getSafeNumber(safeSales.totalSales, 0);
  const targetSales = getSafeNumber(safeSales.targetSales, 0);
  const averageBasket = getSafeNumber(safeSales.averageBasket, 0);
  const conversionRate = getSafeNumber(safeSales.conversionRate, 0);
  const bestProduct = getSafeString(safeSales.bestProduct, 'one cikan urunler');
  const weakProduct = getSafeString(safeSales.weakProduct, 'dusuk performansli urunler');
  const healthScore = calculateCampaignHealth(rows);
  const salesGap = targetSales > 0 ? Math.max(0, targetSales - totalSales) : 0;
  const isSalesWeak = salesScore < 50 || (targetSales > 0 && totalSales < targetSales) || trend === 'down';
  const isInstagramWeak = instagramScore < 50;
  const isDisplayWeak = displayScore < 50;
  const isStaffWeak = staffScore < 50;

  if (isSalesWeak) {
    actions.push({
      channel: 'İNTERNET',
      title: 'Satis performansi dusuk',
      description: `${weakProduct} icin teklif olustur, ${bestProduct} ile sepeti guclendir`
    });
  }
  if (isInstagramWeak) {
    actions.push({
      channel: 'İNSTAGRAM',
      title: 'Instagram etkilesimi zayif',
      description: 'Gunluk reels + story serisi ve DM yonlendirme akisi baslat'
    });
  }
  if (isDisplayWeak) {
    actions.push({
      channel: 'MAĞAZA',
      title: 'Vitrin etkisi dusuk',
      description: 'Vitrin alanini yenile ve dikkat cekici urunleri girise al'
    });
  }
  if (isStaffWeak) {
    actions.push({
      channel: 'PERSONEL',
      title: 'Personel performansi gelistirilmeli',
      description: 'Mini satis egitimi, gunluk hedef ve vardiya bazli takip uygula'
    });
  }

  if (!actions.length) {
    actions.push({
      channel: 'GENEL',
      title: 'Performans dengeli',
      description: 'Mevcut kampanya akisina devam et ve gunluk optimizasyon uygula'
    });
  }

  const urgencyLevel = trend === 'down' || salesGap > 0 ? 'high' : 'normal';
  const safeName = normalizeCampaignName(campaignName);
  const summary =
    urgencyLevel === 'high'
      ? `${safeName} icin acil aksiyon oneriliyor. Satis hedefinden uzak alanlar takip edilmeli.`
      : `${safeName} icin plan dengeli. Kampanya performansi takip edilmeli.`;

  const riskLevel = salesScore < 40 || (targetSales > 0 && totalSales < targetSales * 0.7) ? 'HIGH' : salesScore <= 70 ? 'MEDIUM' : 'LOW';
  const impactParts = [];
  if (isSalesWeak) impactParts.push('Satis tarafinda toparlanma beklenir');
  if (isInstagramWeak) impactParts.push('Dijital gorunurluk artisi beklenir');
  if (isDisplayWeak) impactParts.push('Magaza ici dikkat ve urun gorunurlugu artar');
  if (averageBasket > 0) impactParts.push(`Ortalama sepet ${averageBasket} seviyesine gore optimize edilir`);
  const expectedImpact = impactParts.length
    ? `${impactParts.join('. ')}.`
    : 'Mevcut performansin korunmasi ve kademeli artis beklenir.';

  const insightParts = [];
  if (isSalesWeak) insightParts.push('Satis zayif, teklif ve sepet odakli aksiyon gerekli');
  if (isInstagramWeak) insightParts.push('Instagram gorunurlugu dusuk, icerik akisi guclendirilmeli');
  if (isDisplayWeak) insightParts.push('Vitrin etkisi dusuk, magaza ici yonlendirme yenilenmeli');
  if (isStaffWeak) insightParts.push('Ekip performansi dusuk, gunluk hedef sistemi kurulmalı');
  if (lastCampaignResult !== 'unknown') insightParts.push(`Son kampanya sonucu: ${lastCampaignResult}`);
  const managerInsight = insightParts.length
    ? insightParts.slice(0, 2).join('. ') + '.'
    : 'Kampanya dengeli ilerliyor. Oncelikli alanlara odaklanarak donusum guclendirilebilir.';

  const actionByNeed = [
    { key: 'sales', value: salesScore, text: 'Satis odakli teklif kartlari olustur' },
    { key: 'instagram', value: instagramScore, text: 'Instagram reels serisi baslat' },
    { key: 'staff', value: staffScore, text: 'Personel gunluk hedef sistemi kur' },
    { key: 'display', value: displayScore, text: 'Vitrin kampanya alanini yenile' },
    { key: 'conversion', value: conversionRate || 100, text: 'Web donusum akisini guclendir' }
  ].sort((a, b) => a.value - b.value);

  let priorityActions = actionByNeed.slice(0, 3).map((x) => x.text);
  if (trend === 'down' || isSalesWeak) {
    priorityActions = ['Satis odakli teklif kartlari olustur', ...priorityActions.filter((x) => x !== 'Satis odakli teklif kartlari olustur')].slice(0, 3);
  }

  return {
    smartActions: actions,
    urgencyLevel,
    healthScore,
    summary,
    managerInsight,
    priorityActions,
    riskLevel,
    expectedImpact
  };
};

const getCampaignGoalFromRows = (rows, fallback) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const detail = getSafeString(safeRows.find((row) => getSafeString(row?.kampanyaDetayi).includes('Kampanya amacı'))?.kampanyaDetayi);
  const match = detail.match(/Kampanya amacı:\s*([^\n]+)/);
  return getSafeString(match?.[1], fallback);
};

const buildApprovedPlan = (campaign) => {
  const safeCampaign = campaign && typeof campaign === 'object' ? campaign : {};
  const submitted = getSafeArray(safeCampaign.submittedIdeas)
    .filter((idea) => idea?.approved)
    .map((idea) => getSafeString(idea?.text))
    .filter(Boolean);
  return {
    details: {
      name: getSafeString(safeCampaign.name, 'Yeni Kampanya'),
      start: getSafeString(safeCampaign.start, '-'),
      end: getSafeString(safeCampaign.end, '-'),
      goal: getSafeString(safeCampaign.campaignDetails?.goal, getSafeString(safeCampaign.goal, 'Kampanya hedefi belirtilmedi.')),
      aiGoalText: getSafeString(safeCampaign.campaignDetails?.aiGoalText)
    },
    store: getSafeArray(safeCampaign.storePlan),
    staff: getSafeArray(safeCampaign.staffPlan),
    website: getSafeArray(safeCampaign.websitePlan).length ? getSafeArray(safeCampaign.websitePlan) : getSafeArray(safeCampaign.internetPlan),
    instagram: getSafeArray(safeCampaign.instagramPlan),
    custom: [...getSafeArray(safeCampaign.customIdeas), ...submitted]
  };
};

export const generateStaffOutput = ({ campaignName, rows, salesData, campaign }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeSales = salesData && typeof salesData === 'object' ? salesData : {};
  const safeName = normalizeCampaignName(campaignName);
  const bestProduct = getSafeString(safeSales.bestProduct, 'öne çıkan ürünler');
  const weakProduct = getSafeString(safeSales.weakProduct, 'kampanya ürünleri');
  const campaignGoal = getCampaignGoalFromRows(safeRows, `${bestProduct} ve ${weakProduct} ürünlerini müşteriye daha net anlatmak`);
  const approvedPlan = buildApprovedPlan(campaign);

  return {
    title: `${safeName} - Personel Görev Çıktısı`,
    subtitle: 'Personele Paylaşılacak Kampanya Uygulama Dosyası',
    campaignFile: true,
    summary: `Bu dosya ${safeName} döneminde personelin uygulayacağı onaylanmış kampanya görevlerini gösterir.`,
    campaignDetails: {
      name: approvedPlan.details.name || safeName,
      dateRange: `${approvedPlan.details.start} - ${approvedPlan.details.end}`,
      goal: approvedPlan.details.goal || campaignGoal,
      targetText: approvedPlan.details.goal || campaignGoal
    },
    approvedStorePlan: approvedPlan.store.length
      ? approvedPlan.store
      : ['Kampanya ürünleri mağaza içinde, vitrin ve bankoda görünür şekilde hazırlanacak.'],
    approvedStaffPlan: approvedPlan.staff.length
      ? approvedPlan.staff
      : ['Personel kampanya avantajını müşteriye kısa, net ve aynı dille anlatacak.'],
    approvedCustomIdeas: approvedPlan.custom,
    staffShareNote: 'Bu çıktı personele doğrudan gönderilebilir. Her personel mağaza düzeni, müşteri iletişimi ve kampanya uygulamasını bu plana göre takip eder.',
    keyFocus: `${bestProduct} ve ${weakProduct} ürünleri müşteriye net, kısa ve kampanya hedefiyle uyumlu anlatılacak.`
  };
};

const getActionsByChannel = (actions, channel, fallback) => {
  const safeActions = Array.isArray(actions) ? actions : [];
  const filtered = safeActions
    .filter((action) => getSafeString(action?.channel).toLocaleUpperCase('tr-TR') === channel)
    .map((action) => `${getSafeString(action?.title, 'Aksiyon')}: ${getSafeString(action?.description, '-')}`);
  return filtered.length ? filtered : fallback;
};

const formatCurrencyTR = (value) => `${Math.round(getSafeNumber(value, 0)).toLocaleString('tr-TR')} TL`;
const formatGramTR = (value) => `${getSafeNumber(value, 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} gr`;

export const generateManagerReport = ({ campaignName, rows, storePerformance, salesData, engineOutput, campaign, branchStats, campaignSalesAnalysis }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeStore = storePerformance && typeof storePerformance === 'object' ? storePerformance : {};
  const safeSales = salesData && typeof salesData === 'object' ? salesData : {};
  const safeEngine = engineOutput && typeof engineOutput === 'object' ? engineOutput : {};
  const approvedPlan = buildApprovedPlan(campaign);
  const safeName = normalizeCampaignName(campaignName);
  const salesScore = getSafeNumber(safeStore.salesScore, 0);
  const instagramScore = getSafeNumber(safeStore.instagramScore, 0);
  const staffScore = getSafeNumber(safeStore.staffScore, 0);
  const displayScore = getSafeNumber(safeStore.displayScore, 0);
  const trend = getSafeString(safeSales.trend, 'stable');
  const totalSales = getSafeNumber(safeSales.totalSales, 0);
  const targetSales = getSafeNumber(safeSales.targetSales, 0);
  const conversionRate = getSafeNumber(safeSales.conversionRate, 0);
  const averageBasket = getSafeNumber(safeSales.averageBasket, 0);
  const safeBranchStats = branchStats && typeof branchStats === 'object' ? branchStats : {};
  const branchItems = Array.isArray(safeBranchStats.stats) ? safeBranchStats.stats : [];
  const branchStatusSummary = branchItems.length
    ? branchItems.map((branch) => `${getSafeString(branch.label, 'Şube')}: %${getSafeNumber(branch.rate, 0)} (${getSafeNumber(branch.done, 0)}/${getSafeNumber(branch.total, 0)} görev)`)
    : ['Şube görev verisi henüz oluşmadı.'];
  const sharedTaskCount = approvedPlan.store.length + approvedPlan.staff.length + approvedPlan.custom.length;
  const personnelShareStatus = sharedTaskCount
    ? `Personel çıktısı hazır. Paylaşılacak görev sayısı: ${sharedTaskCount}.`
    : 'Personelle paylaşılacak görev seçilmemiş; önce kampanya detayından görev seçilmeli.';
  const salesAnalysis = campaignSalesAnalysis && typeof campaignSalesAnalysis === 'object' ? campaignSalesAnalysis : {};
  const targetSalesText =
    targetSales > 0
      ? `Kayıtlı hedef ${targetSales.toLocaleString('tr-TR')} TL; sepet ortalaması ${averageBasket.toLocaleString('tr-TR')} TL, dönüşüm %${conversionRate}.`
      : '';
  const resultSummary = salesAnalysis.available
    ? [
        `Takı Ürün kampanya satışı: ${formatCurrencyTR(salesAnalysis.during?.jewelry?.total)} (${getSafeNumber(salesAnalysis.during?.jewelry?.count, 0)} işlem).`,
        `Önceki dönem Takı Ürün: ${formatCurrencyTR(salesAnalysis.before?.jewelry?.total)} (${getSafeNumber(salesAnalysis.before?.jewelry?.count, 0)} işlem).`,
        `Gram kıyas: ${formatGramTR(salesAnalysis.during?.jewelry?.gram)} / önceki ${formatGramTR(salesAnalysis.before?.jewelry?.gram)}.`,
        salesAnalysis.jewelryDiffRate === null
          ? `Takı Ürün farkı: ${formatCurrencyTR(salesAnalysis.jewelryDiff)} / ${formatGramTR(salesAnalysis.jewelryGramDiff)}. Önceki dönemde satış olmadığı için yüzde hesaplanmadı.`
          : `Takı Ürün farkı: ${formatCurrencyTR(salesAnalysis.jewelryDiff)} (%${salesAnalysis.jewelryDiffRate}) / ${formatGramTR(salesAnalysis.jewelryGramDiff)} (${salesAnalysis.jewelryGramDiffRate === null ? 'gram yüzdesi yok' : `%${salesAnalysis.jewelryGramDiffRate}`}).`
      ].join(' ')
    : getSafeString(salesAnalysis.message, 'Gerçek satış verisi henüz okunamadı.');
  const branchSalesSummary = salesAnalysis.available
    ? salesAnalysis.branchResults.map((branch) => {
        const rateText = branch.diffRate === null ? 'yüzde yok' : `%${branch.diffRate}`;
        const gramRateText = branch.gramDiffRate === null ? 'gram yüzdesi yok' : `%${branch.gramDiffRate}`;
        const fourteenRateText = branch.fourteenKaratDiffRate === null ? 'yüzde yok' : `%${branch.fourteenKaratDiffRate}`;
        const fourteenGramRateText = branch.fourteenKaratGramDiffRate === null ? 'gram yüzdesi yok' : `%${branch.fourteenKaratGramDiffRate}`;
        return `${getSafeString(branch.label, 'Şube')}: Takı Ürün ${formatCurrencyTR(branch.during?.jewelry?.total)} / ${formatGramTR(branch.during?.jewelry?.gram)}; önceki ${formatCurrencyTR(branch.before?.jewelry?.total)} / ${formatGramTR(branch.before?.jewelry?.gram)} (${rateText}, ${gramRateText}). 14 Ayar: ${formatCurrencyTR(branch.during?.fourteenKarat?.total)} / ${formatGramTR(branch.during?.fourteenKarat?.gram)} (${fourteenRateText}, ${fourteenGramRateText})`;
      })
    : [getSafeString(salesAnalysis.message, 'Şube satış verisi yok.')];
  const categorySalesSummary = salesAnalysis.available
    ? [
        salesAnalysis.topJewelryProduct
          ? `Takı Ürün içinde öne çıkan: ${salesAnalysis.topJewelryProduct.name} (${formatCurrencyTR(salesAnalysis.topJewelryProduct.value)}).`
          : 'Takı Ürün satışı görünmüyor.',
        salesAnalysis.fourteenKaratDiffRate === null
          ? `14 Ayar takı farkı: ${formatCurrencyTR(salesAnalysis.fourteenKaratDiff)} / ${formatGramTR(salesAnalysis.fourteenKaratGramDiff)}.`
          : `14 Ayar takı farkı: ${formatCurrencyTR(salesAnalysis.fourteenKaratDiff)} (%${salesAnalysis.fourteenKaratDiffRate}) / ${formatGramTR(salesAnalysis.fourteenKaratGramDiff)} (${salesAnalysis.fourteenKaratGramDiffRate === null ? 'gram yüzdesi yok' : `%${salesAnalysis.fourteenKaratGramDiffRate}`}).`,
        salesAnalysis.topFourteenKaratProduct
          ? `14 Ayar öne çıkan ürün: ${salesAnalysis.topFourteenKaratProduct.name} (${formatCurrencyTR(salesAnalysis.topFourteenKaratProduct.value)}).`
          : '14 Ayar takı satışı görünmüyor.'
      ]
    : ['Kategori analizi için gerçek satış verisi okunamadı.'];
  const salesEffectSummary = salesAnalysis.available
    ? salesAnalysis.jewelryDiff > 0
      ? `Gerçek veriye göre Takı Ürün satışı önceki aynı süreye göre arttı. Artış ${formatCurrencyTR(salesAnalysis.jewelryDiff)} ve ${formatGramTR(salesAnalysis.jewelryGramDiff)}${salesAnalysis.jewelryDiffRate === null ? '' : ` (%${salesAnalysis.jewelryDiffRate})`}. ${targetSalesText}`.trim()
      : salesAnalysis.jewelryDiff < 0
        ? `Gerçek veriye göre Takı Ürün satışı önceki aynı sürenin gerisinde. Fark ${formatCurrencyTR(salesAnalysis.jewelryDiff)} ve ${formatGramTR(salesAnalysis.jewelryGramDiff)}${salesAnalysis.jewelryDiffRate === null ? '' : ` (%${salesAnalysis.jewelryDiffRate})`}. ${targetSalesText}`.trim()
        : `Gerçek veriye göre Takı Ürün satışı önceki aynı süreyle aynı seviyede. Gram farkı ${formatGramTR(salesAnalysis.jewelryGramDiff)}. ${targetSalesText}`.trim()
    : getSafeString(salesAnalysis.message, 'Gerçek satış verisi henüz okunamadı.');
  const lastWeekJewelryTotal = getSafeNumber(salesAnalysis.lastWeek?.jewelry?.total, 0);
  const campaignDays = Math.max(1, getSafeNumber(salesAnalysis.campaignDays, 1));
  const dailyJewelryAverage = lastWeekJewelryTotal / 7;
  const suggestedTarget = Math.round(dailyJewelryAverage * campaignDays * 1.2);
  const campaignExpectation = salesAnalysis.available
    ? suggestedTarget > 0
      ? `Son 1 haftalık Takı Ürün satışına göre bu kampanya için önerilen satış hedefi yaklaşık ${formatCurrencyTR(suggestedTarget)}. Bu hedef, haftalık ortalamanın kampanya süresine uyarlanıp %20 büyüme payı eklenmesiyle hesaplandı.`
      : 'Son 1 haftada Takı Ürün satışı görünmediği için net hedef hesaplanamadı; kampanya hedefi şube stok ve teşhir gücüne göre manuel belirlenmeli.'
    : 'Kampanya beklentisi için gerçek satış verisi okunamadı.';
  const aiSalesComment = getSafeString(salesAnalysis.aiComment) || (salesAnalysis.available
    ? salesAnalysis.jewelryDiff > 0
      ? 'AI yorum: Takı Ürün kategorisinde kampanya etkisi pozitif. 14 Ayar ürünlerde öne çıkan model ayrı teşhirle desteklenmeli.'
      : salesAnalysis.jewelryDiff < 0
        ? 'AI yorum: Takı Ürün satışı önceki dönemin gerisinde. 14 Ayar takı teşhiri, personel cümlesi ve şube açık görevleri aynı gün kontrol edilmeli.'
        : 'AI yorum: Takı Ürün satışı önceki dönemle aynı seviyede. Fark yaratmak için 14 Ayar ürünlere ayrı vitrin ve banko odağı verilmeli.'
    : 'AI yorum: Gerçek satış verisi okununca kampanya etkisi otomatik yorumlanacak.');
  const campaignGoal = getCampaignGoalFromRows(safeRows, `${safeName} ile satış, görünürlük ve müşteri ilgisini artırmak`);
  const healthScore = getSafeNumber(safeEngine.healthScore, calculateCampaignHealth(safeRows));
  const riskLevel = getSafeString(
    safeEngine.riskLevel,
    salesScore < 40 || (targetSales > 0 && totalSales < targetSales * 0.7) ? 'HIGH' : salesScore <= 70 ? 'MEDIUM' : 'LOW'
  );
  const urgencyLevel = getSafeString(safeEngine.urgencyLevel, trend === 'down' ? 'high' : 'normal');
  const smartActions = Array.isArray(safeEngine.smartActions) ? safeEngine.smartActions : [];
  const expectedImpact = salesAnalysis.available
    ? salesAnalysis.jewelryDiff > 0
      ? 'Gerçek veriler Takı Ürün tarafında pozitif etki gösteriyor. Kampanya süresince 14 Ayar ürün odağı korunursa etki büyüyebilir.'
      : salesAnalysis.jewelryDiff < 0
        ? 'Gerçek veriler Takı Ürün tarafında beklenen artışı göstermiyor. 14 Ayar ürün teşhiri, personel öneri dili ve açık şube görevleri hızla güçlendirilmeli.'
        : 'Gerçek veriler Takı Ürün satışında nötr etki gösteriyor. Kampanya farkı yaratmak için 14 Ayar ürünlere ayrı banko ve vitrin odağı verilmeli.'
    : getSafeString(safeEngine.expectedImpact, 'Gerçek satış verisi okununca beklenen etki otomatik yorumlanacak.');

  return {
    title: `${safeName} - Yönetici Kampanya Raporu`,
    campaignDetails: {
      name: approvedPlan.details.name || safeName,
      dateRange: `${approvedPlan.details.start} - ${approvedPlan.details.end}`,
      goal: approvedPlan.details.goal || campaignGoal
    },
    summary:
      `Kampanya hedefi: ${approvedPlan.details.goal || campaignGoal}. Onaylanan uygulamalar mağaza, personel ve dijital kanallarda sade şekilde takip edilecek.`,
    healthScore,
    riskLevel,
    urgencyLevel,
    managerInsight: getSafeString(
      safeEngine.managerInsight,
      'Yönetici için ana takip: seçilen fikirlerin sahada uygulanması, personel dilinin aynı kalması ve açık görevlerin gün sonunda kapanması.'
    ),
    personnelShareStatus,
    branchStatusSummary,
    salesEffectSummary,
    resultSummary,
    branchSalesSummary,
    categorySalesSummary,
    aiSalesComment,
    campaignExpectation,
    storeActions: approvedPlan.store.length ? approvedPlan.store : getActionsByChannel(smartActions, 'MAĞAZA', [
      displayScore < 50 ? 'Vitrin ve giriş alanı kampanya ürünlerine göre yeniden düzenlenmeli.' : 'Mağaza kampanya alanı günlük kontrol edilmeli.',
      'Etiket, stok ve ürün görünürlüğü her gün açılışta kontrol edilmeli.'
    ]),
    staffActions: approvedPlan.staff.length ? approvedPlan.staff : getActionsByChannel(smartActions, 'PERSONEL', [
      staffScore < 50 ? 'Personel için kısa satış briefi ve günlük hedef takibi yapılmalı.' : 'Personel kampanya mesajını aynı dilde anlatmalı.',
      'Gün sonu geri bildirimleri yöneticiye kısa şekilde aktarılmalı.'
    ]),
    internetActions: approvedPlan.website.length ? approvedPlan.website : getActionsByChannel(smartActions, 'İNTERNET', [
      'Web banner, ürün görünürlüğü ve kampanya açıklamaları kontrol edilmeli.',
      trend === 'down' ? 'Düşen satış trendi için teklif ve sepet odaklı ürünler öne alınmalı.' : 'Dijital satış akışı günlük izlenmeli.'
    ]),
    instagramActions: approvedPlan.instagram.length ? approvedPlan.instagram : getActionsByChannel(smartActions, 'İNSTAGRAM', [
      instagramScore < 50 ? 'Reels, story ve DM yönlendirme akışı güçlendirilmeli.' : 'Instagram içerik akışı kampanya boyunca düzenli sürdürülmeli.',
      'Günlük paylaşım sonrası gelen müşteri soruları hızlı yanıtlanmalı.'
    ]),
    customIdeas: approvedPlan.custom,
    expectedImpact
  };
};

